import { useMemo, useState } from "react";

import { ContactMatchingPhoneNumberResult } from "@/Common/Api/CallCenter/Responses/ContactMatchingPhoneNumberResult";
import { IndividualMatchingPhoneNumberComposite } from "@/Common/Api/CallCenter/Responses/IndividualMatchingPhoneNumberComposite";
import { Guid } from "@/Common/Models/Guid";
import { ContactMatchingPhoneNumberSearchableText } from "@/Common/Utilities/ContactMatchingPhoneNumberSearchableText";
import { ContactMatchingPhoneNumberSearchFilter } from "@/Common/Utilities/ContactMatchingPhoneNumberSearchFilter";
import { TextboxControl } from "../Inputs/TextboxControl";
import { MatchedIndividualListItem } from "./MatchedIndividualListItem";

import styles from "./MatchedIndividualList.module.scss";

interface IMatchedIndividualListProps {
    matches: ContactMatchingPhoneNumberResult[];
    profilePictureVersion: Guid;
    onItemClick: (contactMatching: ContactMatchingPhoneNumberResult) => Promise<void>;
}

function MatchedIndividualList(props: Readonly<IMatchedIndividualListProps>): JSX.Element {
    const [searchText, setSearchText] = useState("");

    const { matches, onItemClick } = props;
    const individualLookup = matches?.map(result => result.Individual).toDictionary(item => item.Contact_id);
    const showSearchHeader = matches.length >= 4;

    const searchFilter = useMemo(
        () => showSearchHeader
            ? new ContactMatchingPhoneNumberSearchFilter(
                matches.map(match => new ContactMatchingPhoneNumberSearchableText(match))
            )
            : null
        , [matches, showSearchHeader]
    );

    function onSearchChanged(text: any): void {
        setSearchText(text);
    }

    function getLinkedIndividual(contactId: Guid): IndividualMatchingPhoneNumberComposite {
        return individualLookup[contactId];
    }

    function getSearchHeader(): JSX.Element {
        if (!showSearchHeader) {
            return null;
        }
        const matchCountLabel = cacheStore.GetString("CallCenter_PatientMatchesFound", { count: matches.length });
        const searchPlaceholderLabel = cacheStore.GetString("CallCenter_SearchMatchesPrompt");
        return (
            <div className={styles.SearchBanner}>
                <div className={styles.MatchCount}>{matchCountLabel}</div>
                <div className={styles.SearchTextbox}>
                    <label htmlFor="CallCenterMatchesSearch" className={styles.SearchGlyph}>
                        <span className="glyphicon glyphicon-search" />
                    </label>
                    <TextboxControl
                        id="CallCenterMatchesSearch"
                        placeholder={searchPlaceholderLabel}
                        style={{ height: "37px", paddingLeft: "30px" }}
                        onChange={onSearchChanged}
                        value={searchText}
                    />
                </div>
            </div>
        );
    }

    function getListItem(result: ContactMatchingPhoneNumberResult): JSX.Element {
        return (
            <MatchedIndividualListItem
                ResultItem={result}
                profilePictureVersion={props.profilePictureVersion}
                getLinkedIndividual={getLinkedIndividual}
                key={result.Individual.Contact_id}
                onItemClick={onItemClick}
            />
        );
    }

    function getItemsToShow(): JSX.Element {
        const itemsToShow = showSearchHeader
            ? searchFilter.GetItemsContaining(searchText)
                .map(result => result.ContactMatching)
            : matches;
        const listClass = showSearchHeader ? styles.ResultsListLimited : styles.ResultsList;
        return (
            <div className={listClass}>
                {itemsToShow.map(getListItem)}
            </div>
        );
    }

    const searchHeader = getSearchHeader();
    const itemsToShow = getItemsToShow();

    return (
        <div>
            {searchHeader}
            {itemsToShow}
        </div>
    );
}

export { IMatchedIndividualListProps, MatchedIndividualList };
